/**
 * Componentes DocumentUpload, AccountingUpload, XMLUpload, LinkUpload que permite llevar acabo todas las operaciones de carga de archivos
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { Upload, notification, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { upload, linking, accounting, xml } from '../../actions/storage/upload';
import { isSystemFile } from './system_files';

// beforeUpload compartido: descarta archivos del SO (.DS_Store, thumbs.db,
// desktop.ini, etc.) antes de encolarlos. Si el usuario arrastra una carpeta
// completa, evita que esos archivos basura lleguen a Storage y a Firestore.
const skipSystemFiles = (file) => {
  if (isSystemFile(file)) {
    notification.info({
      message: 'Archivo omitido',
      description: `"${file.name}" es un archivo del sistema operativo y no se subirá.`,
      placement: 'topRight',
    });
    return Upload.LIST_IGNORE;
  }
  return true;
};

class FileUploadQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
  }

  enqueue(file, callback) {
    this.queue.push({ file, callback });
    this.processNext();
  }

  async processNext() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;
    const { file, callback } = this.queue.shift();

    try {
      await callback(file);
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    this.processing = false;
    this.processNext();
  }
}

const fileUploadQueue = new FileUploadQueue();

/**
 * Exporta el componente DocumentUpload
 */
export const DocumentUpload = ({ reference, onAddFile }) => {
  const dispatch = useDispatch();

  const onChangeUpload = info => {
    switch (info.file.status) {
      case 'uploading':
        notification.info({
          message: 'Cargando',
          description: `Se está cargando el archivo ${info.file.name}.`,
        });
        break;
      case 'done':
        notification.success({
          message: 'Cargado',
          description: `El archivo ${info.file.name} fue cargado correctamente.`,
        });
        break;
      case 'error':
        notification.error({
          message: 'Error',
          description: `Error al cargar el archivo ${info.file.name}.`,
        });
        break;
      default:
        break;
    }
  };

  const onUpload = file => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(upload(file.file, reference))
          .then(() => {
            file.onSuccess();
            onAddFile();
            resolve();
          })
          .catch(() => {
            file.onError();
            reject();
          });
      } catch (error) {
        file.onError();
        reject();
      }
    });
  };

  const handleCustomRequest = options => {
    fileUploadQueue.enqueue(options, onUpload);
  };

  return (
    <Upload
      name='file'
      multiple={true}
      beforeUpload={skipSystemFiles}
      customRequest={handleCustomRequest}
      showUploadList={false}
      onChange={onChangeUpload}
    >
      <Button type='dashed'>
        <UploadOutlined /> Subir archivos
      </Button>
    </Upload>
  );
};

/**
 * Exporta el componente AccountingUpload
 */
export const AccountingUpload = ({ reference, onAccounting }) => {
  const dispatch = useDispatch();

  const onChangeUpload = info => {
    switch (info.file.status) {
      case 'uploading':
        notification.info({
          message: 'Cargando',
          description: `Se está cargando el archivo ${info.file.name}.`,
        });
        break;
      case 'done':
        notification.success({
          message: 'Cargado',
          description: `El archivo ${info.file.name} fue cargado correctamente.`,
        });
        break;
      case 'error':
        notification.error({
          message: 'Error',
          description: `Error al cargar el archivo ${info.file.name}.`,
        });
        break;
      default:
        break;
    }
  };

  const onUpload = file => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(accounting(file.file, reference))
          .then(() => {
            file.onSuccess();
            onAccounting();
            resolve();
          })
          .catch(() => {
            file.onError();
            reject();
          });
      } catch (error) {
        file.onError();
        reject();
      }
    });
  };

  const handleCustomRequest = options => {
    fileUploadQueue.enqueue(options, onUpload);
  };

  return (
    <Upload
      name='file'
      multiple={true}
      beforeUpload={skipSystemFiles}
      customRequest={handleCustomRequest}
      // Sin lista propia: las subidas aparecen en la tabla del tab (el
      // documento admite varias contabilizaciones; la cola las procesa
      // en serie y el thunk hace append sobre el doc fresco).
      showUploadList={false}
      onChange={onChangeUpload}
    >
      <Button type='primary'>
        <UploadOutlined /> Subir contabilización
      </Button>
    </Upload>
  );
};

/**
 * Exporta el componente XMLUpload
 */
export const XMLUpload = ({ reference, onXML }) => {
  const dispatch = useDispatch();

  const onChangeUpload = info => {
    switch (info.file.status) {
      case 'uploading':
        notification.info({
          message: 'Cargando',
          description: `Se está cargando el archivo ${info.file.name}.`,
        });
        break;
      case 'done':
        notification.success({
          message: 'Cargado',
          description: `El archivo ${info.file.name} fue cargado correctamente.`,
        });
        break;
      case 'error':
        notification.error({
          message: 'Error',
          description: `Error al cargar el archivo ${info.file.name}.`,
        });
        break;
      default:
        break;
    }
  };

  const onUpload = file => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(xml(file.file, reference))
          .then(() => {
            file.onSuccess();
            onXML();
            resolve();
          })
          .catch(() => {
            file.onError();
            reject();
          });
      } catch (error) {
        file.onError();
        reject();
      }
    });
  };

  const handleCustomRequest = options => {
    fileUploadQueue.enqueue(options, onUpload);
  };

  return (
    <Upload
      name='file'
      accept='text/xml'
      multiple={false}
      beforeUpload={skipSystemFiles}
      customRequest={handleCustomRequest}
      showUploadList={true}
      onChange={onChangeUpload}
    >
      <Button type='primary'>
        <UploadOutlined /> Subir archivo XML
      </Button>
    </Upload>
  );
};

/**
 * Exporta el componente LinkUpload
 */
export const LinkUpload = ({ reference, onLinking, onPreview, loading }) => {
  const dispatch = useDispatch();

  const onChangeUpload = info => {
    switch (info.file.status) {
      case 'uploading':
        notification.info({
          message: 'Cargando',
          description: `Se está cargando el archivo ${info.file.name}.`,
        });
        break;
      case 'done':
        notification.success({
          message: 'Cargado',
          description: `El archivo ${info.file.name} fue cargado correctamente.`,
        });
        break;
      case 'error':
        notification.error({
          message: 'Error',
          description: `Error al cargar el archivo ${info.file.name}.`,
        });
        break;
      default:
        break;
    }
  };

  const onUpload = file => {
    return new Promise((resolve, reject) => {
      try {
        onPreview();
        dispatch(linking(file.file, reference))
          .then(() => {
            file.onSuccess();
            onLinking();
            resolve();
          })
          .catch(() => {
            file.onError();
            reject();
          });
      } catch (error) {
        file.onError();
        reject();
      }
    });
  };

  const handleCustomRequest = options => {
    fileUploadQueue.enqueue(options, onUpload);
  };

  return (
    <Upload
      name='file'
      multiple={true}
      disabled={loading}
      beforeUpload={skipSystemFiles}
      customRequest={handleCustomRequest}
      showUploadList={false}
      onChange={onChangeUpload}
    >
      <Button type='primary' loading={loading} style={{ marginBottom: 16 }}>
        <UploadOutlined /> Vincular archivos
      </Button>
    </Upload>
  );
};
